import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Play, ChevronRight, Tv, Users, Mic, BookOpen } from 'lucide-react';
import axios from 'axios';
import ShowCard from '../components/ShowCard';
import VideoPlayer from '../components/VideoPlayer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOGO_URL = "https://customer-assets.emergentagent.com/job_cheat-alerts/artifacts/gnd74fuw_Copy%20of%20CheaterBeaters%20%28Stickers%20%28Circle%29%29.jpg";
const CBX_LOGO_URL = "https://customer-assets.emergentagent.com/job_cheat-alerts/artifacts/ih9iw88h_cbxtransp.png";

// Fallback mock data in case backend has no data yet
const fallbackShows = [
  {
    id: '1',
    title: "Caught Red Handed: Season 3 Premiere",
    thumbnail: "https://images.unsplash.com/photo-1485846234645-a62644f84728?w=600&h=400&fit=crop",
    youtubeId: "dQw4w9WgXcQ",
    category: "New Episode",
    views: "1.2M",
    duration: "45:00"
  },
  {
    id: '2',
    title: "The Ultimate Betrayal - Special Episode",
    thumbnail: "https://images.unsplash.com/photo-1536440136628-849c177e76a1?w=600&h=400&fit=crop",
    youtubeId: "dQw4w9WgXcQ",
    category: "Special",
    views: "890K",
    duration: "52:00"
  },
  {
    id: '3',
    title: "Double Life Exposed: Part 1",
    thumbnail: "https://images.unsplash.com/photo-1478720568477-152d9b164e26?w=600&h=400&fit=crop",
    youtubeId: "dQw4w9WgXcQ",
    category: "Drama",
    views: "2.1M",
    duration: "38:00"
  },
  {
    id: '4',
    title: "When Trust Is Broken",
    thumbnail: "https://images.unsplash.com/photo-1440404653325-ab127d49abc1?w=600&h=400&fit=crop",
    youtubeId: "dQw4w9WgXcQ",
    category: "Documentary",
    views: "567K",
    duration: "41:00"
  },
  {
    id: '5',
    title: "Secret Affairs: The Investigation",
    thumbnail: "https://images.unsplash.com/photo-1489599849927-2ee91cede3ba?w=600&h=400&fit=crop",
    youtubeId: "dQw4w9WgXcQ",
    category: "Investigation",
    views: "1.5M",
    duration: "48:00"
  },
  {
    id: '6',
    title: "The Truth Comes Out",
    thumbnail: "https://images.unsplash.com/photo-1524712245354-2c4e5e7121c0?w=600&h=400&fit=crop",
    youtubeId: "dQw4w9WgXcQ",
    category: "Exclusive",
    views: "3.2M",
    duration: "55:00"
  }
];

const fallbackFeaturedShow = {
  id: '0',
  title: "Cheater Beaters: New Season Premiere",
  description: "Get a front row seat to all the madness, drama, and nonstop entertainment. Watch as our team uncovers the truth and exposes the lies. REAL. RAW. UNFILTERED.",
  thumbnail: "https://images.unsplash.com/photo-1594909122845-11baa439b7bf?w=1200&h=600&fit=crop",
  youtubeId: "dQw4w9WgXcQ",
  category: "Season Premiere",
  releaseDate: "October 2025"
};

const podcastInfo = {
  title: "Dear Cheater Beaters",
  description: "Dear Cheater Beaters is a companion podcast to the TV show that takes you deeper into the lives of characters from the show and beyond. Our hosts talk to interesting people who have cheated and been cheated on as they recount their wild stories.",
  callToAction: "If you are the Cheater, and want to appear on the podcast. Just leave us your Cheating story and we might feature it on our 'Oh No You Didn't' section of the podcast. Tell us how you got caught and the situation and maybe we will give you a call."
};

const HomePage = () => {
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [shows, setShows] = useState([]);
  const [stories, setStories] = useState([]);
  const [featuredShow, setFeaturedShow] = useState(fallbackFeaturedShow);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      // Fetch shows
      const showsRes = await axios.get(`${API}/shows`);
      if (showsRes.data && showsRes.data.length > 0) {
        setShows(showsRes.data);
        setFeaturedShow(showsRes.data[0]);
      } else {
        setShows(fallbackShows);
      }

      // Fetch public stories
      const storiesRes = await axios.get(`${API}/stories/public`);
      setStories(storiesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      setShows(fallbackShows);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Premiere Announcement Banner */}
      <div className="bg-gradient-to-r from-red-600 via-red-500 to-red-600 text-white py-3 mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-6 text-center">
            <div className="flex items-center gap-3">
              <span className="animate-pulse bg-white text-red-600 px-3 py-1 rounded-full text-xs font-bold uppercase">Premieres</span>
              <span className="font-bold text-lg">March 11th at 8PM CST</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-red-200">Watch on</span>
              <a href="https://cbxtv.com" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 bg-white/20 hover:bg-white/30 px-3 py-1 rounded-lg transition-colors">
                <img src={CBX_LOGO_URL} alt="CBX TV" className="h-9 object-contain" />
                <span className="font-bold">CBXTV.COM</span>
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <section className="relative min-h-[85vh] flex items-center bg-gradient-to-br from-gray-900 via-gray-800 to-black overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }} />
        </div>

        {/* Red Accent Line */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-red-600" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 w-full">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="text-white">
              <div className="flex items-center gap-4 mb-6">
                <img src={LOGO_URL} alt="Cheater Beaters" className="h-28 w-28 object-contain rounded-full" />
                <div className="h-16 w-px bg-white/30" />
                <div className="flex items-center gap-2">
                  <span className="text-gray-400 text-sm">Streaming on</span>
                  <a href="https://cbxtv.com" target="_blank" rel="noopener noreferrer">
                    <img src={CBX_LOGO_URL} alt="CBX TV" className="h-14 object-contain hover:opacity-80 transition-opacity" />
                  </a>
                </div>
              </div>
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-black leading-tight mb-6">
                <span className="text-red-500">REAL</span> RAW &<br />
                <span className="text-red-500">UNFILTERED</span>
              </h1>
              <p className="text-xl md:text-2xl text-gray-300 mb-4 leading-relaxed">
                Shows, Movies & More. Get a front row seat to all the <span className="text-red-400 font-semibold">madness</span>, <span className="text-red-400 font-semibold">drama</span>, and nonstop entertainment.
              </p>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg px-4 py-3 mb-8 inline-flex items-center gap-3">
                <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
                <span className="text-white font-semibold">Premieres March 11th at 8PM CST on <a href="https://cbxtv.com" target="_blank" rel="noopener noreferrer" className="text-red-400 hover:text-red-300 transition-colors">CBXTV.COM</a></span>
              </div>
              <div className="flex flex-wrap gap-4">
                <a
                  href="https://cbxtv.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-center gap-3 bg-red-600 hover:bg-red-700 text-white px-8 py-4 rounded-lg font-bold text-lg transition-all duration-300 shadow-lg hover:shadow-red-500/30"
                >
                  <Play size={24} fill="white" className="group-hover:scale-110 transition-transform" />
                  Watch on CBX TV
                </a>
                <Link
                  to="/submit"
                  className="flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white px-8 py-4 rounded-lg font-bold text-lg transition-all duration-300 border border-white/20"
                >
                  Share Your Story
                  <ChevronRight size={20} />
                </Link>
              </div>
            </div>

            {/* Right - Featured Video Card */}
            <div 
              className="relative cursor-pointer group"
              onClick={() => setSelectedVideo(featuredShow)}
            >
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src={featuredShow.thumbnail}
                  alt={featuredShow.title}
                  className="w-full aspect-video object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                
                {/* Play Button */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-20 h-20 rounded-full bg-red-600 flex items-center justify-center transform group-hover:scale-110 transition-all duration-300 shadow-2xl ring-4 ring-white/30">
                    <Play size={36} className="text-white ml-1" fill="white" />
                  </div>
                </div>

                {/* Info */}
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="bg-red-600 text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wide">
                      {featuredShow.category}
                    </span>
                    <span className="bg-white/20 text-white text-xs font-bold px-3 py-1 rounded-full">
                      March 11th • 8PM CST
                    </span>
                  </div>
                  <h3 className="text-white text-xl font-bold mt-2">{featuredShow.title}</h3>
                  <div className="flex items-center gap-2 mt-2">
                    <img src={CBX_LOGO_URL} alt="CBX TV" className="h-7 object-contain" />
                    <span className="text-gray-300 text-sm">CBXTV.COM</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 rounded-full border-2 border-white/30 flex items-start justify-center p-2">
            <div className="w-1 h-3 bg-red-500 rounded-full animate-pulse" />
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { icon: Tv, label: 'Watch Shows', desc: 'Stream episodes' },
              { icon: Users, label: 'Real Stories', desc: 'From real people' },
              { icon: Mic, label: 'Podcast', desc: 'Listen now' },
              { icon: BookOpen, label: 'Share', desc: 'Your story' }
            ].map((item, i) => (
              <div key={i} className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow text-center group">
                <div className="w-14 h-14 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4 group-hover:bg-red-600 transition-colors">
                  <item.icon size={24} className="text-red-600 group-hover:text-white transition-colors" />
                </div>
                <h3 className="font-bold text-gray-900">{item.label}</h3>
                <p className="text-gray-500 text-sm mt-1">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Latest Shows Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-10">
            <div>
              <h2 className="text-3xl md:text-4xl font-black text-gray-900">
                Latest <span className="text-red-600">Shows</span>
              </h2>
              <p className="text-gray-600 mt-2">Watch the latest episodes and exclusive content</p>
            </div>
            <Link
              to="/shows"
              className="hidden md:flex items-center gap-2 text-red-600 font-semibold hover:gap-3 transition-all"
            >
              View All <ChevronRight size={20} />
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {shows.slice(0, 6).map((show) => (
              <ShowCard key={show.id} show={show} onPlay={setSelectedVideo} />
            ))}
          </div>

          <div className="mt-8 text-center md:hidden">
            <Link
              to="/shows"
              className="inline-flex items-center gap-2 text-red-600 font-semibold"
            >
              View All Shows <ChevronRight size={20} />
            </Link>
          </div>
        </div>
      </section>

      {/* Podcast Section */}
      <section className="py-20 bg-gradient-to-r from-red-600 to-red-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="text-white">
              <span className="text-red-200 text-sm font-semibold uppercase tracking-wide">New Podcast</span>
              <h2 className="text-4xl md:text-5xl font-black mt-2 mb-6">
                Dear Cheater Beaters
              </h2>
              <p className="text-red-100 text-lg leading-relaxed mb-6">
                {podcastInfo.description}
              </p>
              <p className="text-white/80 mb-8">
                {podcastInfo.callToAction}
              </p>
              <Link
                to="/podcast"
                className="inline-flex items-center gap-2 bg-white text-red-600 px-8 py-4 rounded-lg font-bold text-lg hover:bg-red-50 transition-colors shadow-lg"
              >
                <Mic size={20} />
                Listen Now
              </Link>
            </div>

            <div className="relative">
              <div className="bg-white/10 backdrop-blur rounded-2xl p-8">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-16 h-16 rounded-full bg-white flex items-center justify-center">
                    <Mic size={28} className="text-red-600" />
                  </div>
                  <div>
                    <h3 className="text-white font-bold text-xl">Dear Cheater Beaters</h3>
                    <p className="text-red-200">Weekly Episodes</p>
                  </div>
                </div>
                <div className="space-y-3">
                  {['Ep. 45: The Double Life', 'Ep. 44: Caught at the Airport', 'Ep. 43: 10 Years of Lies'].map((ep, i) => (
                    <div key={i} className="bg-white/10 rounded-lg p-4 flex items-center gap-4 hover:bg-white/20 transition-colors cursor-pointer">
                      <div className="w-10 h-10 rounded-full bg-red-500 flex items-center justify-center flex-shrink-0">
                        <Play size={16} fill="white" className="text-white ml-0.5" />
                      </div>
                      <span className="text-white font-medium">{ep}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Recent Stories Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-black text-gray-900">
              Recent <span className="text-red-600">Stories</span>
            </h2>
            <p className="text-gray-600 mt-3 max-w-2xl mx-auto">
              Real stories from real people. Share your experience and help others.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {(stories.length > 0 ? stories : [
              { id: '1', title: 'Found Out After 10 Years', excerpt: 'After a decade of marriage, I discovered text messages that changed everything...', author: 'Anonymous', date: 'July 12, 2025', views: 15420 },
              { id: '2', title: 'The Double Life', excerpt: 'He had two families in different states. Neither knew about the other...', author: 'Sarah M.', date: 'July 10, 2025', views: 28930 },
              { id: '3', title: 'Caught at the Airport', excerpt: 'My best friend spotted my husband boarding a flight with another woman...', author: 'Anonymous', date: 'July 8, 2025', views: 34210 },
              { id: '4', title: 'The Wedding Crasher', excerpt: 'His ex showed up at our wedding with evidence I couldn\'t ignore...', author: 'Jennifer K.', date: 'July 5, 2025', views: 52100 }
            ]).slice(0, 4).map((story) => (
              <div
                key={story.id}
                className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow border-l-4 border-red-600"
              >
                <div className="flex items-center justify-between mb-3">
                  <span className="text-red-600 font-semibold text-sm">{story.author}</span>
                  <span className="text-gray-400 text-sm">{story.date}</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{story.title}</h3>
                <p className="text-gray-600 line-clamp-2">{story.excerpt}</p>
                <div className="mt-4 flex items-center justify-between">
                  <span className="text-gray-400 text-sm">{(story.views || 0).toLocaleString()} views</span>
                  <button className="text-red-600 font-semibold text-sm hover:text-red-700 transition-colors">
                    Read More
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-10">
            <Link
              to="/submit"
              className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-8 py-4 rounded-lg font-bold text-lg transition-colors shadow-lg"
            >
              Share Your Story
              <ChevronRight size={20} />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex items-center justify-center gap-4 mb-6">
            <img src={CBX_LOGO_URL} alt="CBX TV" className="h-16 object-contain" />
          </div>
          <h2 className="text-3xl md:text-4xl font-black text-white mb-4">
            Watch Cheater Beaters on <span className="text-red-500">CBX TV</span>
          </h2>
          <p className="text-2xl text-red-400 font-bold mb-4">
            Premieres March 11th at 8PM CST
          </p>
          <p className="text-gray-400 text-lg mb-8 max-w-2xl mx-auto">
            Don't miss the premiere! Stream Cheater Beaters exclusively on CBXTV.COM. Have a story to share? Submit it and you might be featured on our show or podcast.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="https://cbxtv.com"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 bg-red-600 hover:bg-red-700 text-white px-10 py-5 rounded-lg font-bold text-xl transition-all duration-300 shadow-lg hover:shadow-red-500/30"
            >
              <Play size={24} fill="white" />
              Watch on CBXTV.COM
            </a>
            <Link
              to="/submit"
              className="inline-flex items-center gap-3 bg-white/10 hover:bg-white/20 text-white px-10 py-5 rounded-lg font-bold text-xl transition-all duration-300 border border-white/20"
            >
              Submit Your Story
              <ChevronRight size={24} />
            </Link>
          </div>
        </div>
      </section>

      {/* Video Player Modal */}
      {selectedVideo && (
        <VideoPlayer
          youtubeId={selectedVideo.youtubeId}
          title={selectedVideo.title}
          onClose={() => setSelectedVideo(null)}
        />
      )}
    </div>
  );
};

export default HomePage;
